import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Sparkles, Loader2, X } from 'lucide-react';
import api from '../../services/api';
import { formatMonto, formatFecha } from '../../utils/format';
import { pref } from '../../utils/moneda';

// ============================================================
// Reglas de categorización automática (#80, maqueta aprobada por Armando
// 2026-10-14: https://claude.ai/artifact/GgQnvuNnKAdFmSN8TPyLBo). Pestaña
// "Reglas" de Cuentas contables, por empresa:
//   - lista de reglas "si contiene X → categoría (cuenta contable)";
//   - nueva/editar regla con vista previa de a qué movimientos afecta;
//   - "Aplicar a los sin categoría": propuesta por regla (marcada) y, para
//     el resto, sugerencia de la IA (sin marcar); se revisa antes.
// La coincidencia la decide el servidor (services/categorizacionReglas.js).
// ============================================================

const CAMPOS = {
  descripcion_beneficiario: 'Descripción o beneficiario',
  descripcion: 'Descripción',
  beneficiario: 'Beneficiario',
  referencia: 'Referencia'
};

const FORM_VACIO = { campo: 'descripcion_beneficiario', texto: '', categoria_id: '', cuenta_id: '', monto_min: '', monto_max: '' };

const inputCls = 'border rounded-md px-3 py-2 text-sm w-full';

function esperar(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// "5.04.02 Gastos Operativos › Gas" para mostrar y elegir.
function nombreCategoria(c, porId) {
  if (!c) return '';
  const padre = c.padre_id ? porId.get(c.padre_id) : null;
  return `${c.codigo ? `${c.codigo} ` : ''}${padre ? `${padre.nombre} › ` : ''}${c.nombre}`;
}

function Etiqueta({ origen }) {
  return origen === 'ia'
    ? <span className="text-[11px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-violet-50 text-violet-700">IA</span>
    : <span className="text-[11px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-green-50 text-green-700">Regla</span>;
}

export default function ReglasCategorizacion({ categorias, cuentas, puede, prefill, onPrefillUsado }) {
  const [reglas, setReglas] = useState([]);
  const [form, setForm] = useState(null);
  const [vista, setVista] = useState(null);
  const [aplicarSin, setAplicarSin] = useState(true);
  const [cambiarOtras, setCambiarOtras] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [lote, setLote] = useState(null); // { total, movimientos } cuando está abierto
  const [totalSin, setTotalSin] = useState(null);
  const [marcados, setMarcados] = useState({});
  const [propuestasIA, setPropuestasIA] = useState({});
  const [pidiendoIA, setPidiendoIA] = useState(false);

  const porId = useMemo(() => new Map(categorias.map((c) => [c.id, c])), [categorias]);
  const opciones = useMemo(() => ['egreso', 'ingreso'].map((tipo) => ({
    tipo,
    items: categorias.filter((c) => c.tipo === tipo).map((c) => ({ id: c.id, texto: nombreCategoria(c, porId) }))
  })), [categorias, porId]);

  const cargar = () => api.get('/categorizacion-reglas').then(({ data }) => setReglas(data));
  const contarSin = () => api.get('/categorizacion-reglas/lote').then(({ data }) => setTotalSin(data.total)).catch(() => {});

  useEffect(() => {
    cargar();
    if (puede) contarSin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Crear regla" desde un movimiento o desde el cierre llega ya lleno.
  const clavePrefill = prefill ? `${prefill.texto}|${prefill.categoria_id}` : '';
  useEffect(() => {
    if (!prefill || !puede) return;
    abrirForm({ ...FORM_VACIO, texto: prefill.texto || '', categoria_id: prefill.categoria_id || '' });
    onPrefillUsado?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clavePrefill]);

  function abrirForm(valores) {
    setForm(valores);
    setVista(null);
    setAplicarSin(true);
    setCambiarOtras(false);
    setError('');
    setAviso('');
    setLote(null);
  }

  // Vista previa mientras se escribe (pausa de 400 ms).
  useEffect(() => {
    if (!form || form.texto.trim().length < 2 || !form.categoria_id) { setVista(null); return undefined; }
    const t = setTimeout(() => {
      api.post('/categorizacion-reglas/vista-previa', form)
        .then(({ data }) => setVista(data))
        .catch(() => setVista(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form]);

  async function guardar() {
    setGuardando(true);
    setError('');
    try {
      let reglaId = form.id;
      if (form.id) await api.put(`/categorizacion-reglas/${form.id}`, form);
      else ({ data: { id: reglaId } } = await api.post('/categorizacion-reglas', form));
      let texto = 'Regla guardada.';
      const items = [
        ...(aplicarSin && vista ? vista.sin_categoria.filter((m) => !m.en_mes_cerrado) : []),
        ...(cambiarOtras && vista ? vista.otras.filter((m) => !m.en_mes_cerrado).map((m) => ({ ...m, cambiar: true })) : [])
      ].map((m) => ({ movimiento_id: m.id, categoria_id: form.categoria_id, regla_id: reglaId, cambiar: !!m.cambiar }));
      if (items.length) {
        const { data } = await api.post('/categorizacion-reglas/aplicar', { items });
        texto = `Regla guardada. ${data.aplicados} movimiento${data.aplicados === 1 ? '' : 's'} con su categoría puesta${data.saltados ? ` (${data.saltados} saltados)` : ''}.`;
      }
      setForm(null);
      setVista(null);
      setAviso(texto);
      await cargar();
      contarSin();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la regla');
    } finally {
      setGuardando(false);
    }
  }

  async function alternar(r) {
    await api.patch(`/categorizacion-reglas/${r.id}/activa`, { activa: !r.activa });
    cargar();
  }

  async function eliminar(r) {
    if (!window.confirm(`¿Eliminar la regla «${r.texto}»? Los movimientos ya categorizados no cambian.`)) return;
    await api.delete(`/categorizacion-reglas/${r.id}`);
    cargar();
  }

  async function abrirLote() {
    setForm(null);
    setAviso('');
    setError('');
    setPropuestasIA({});
    const { data } = await api.get('/categorizacion-reglas/lote');
    setLote(data);
    setTotalSin(data.total);
    setMarcados(Object.fromEntries(data.movimientos.filter((m) => m.propuesta && !m.en_mes_cerrado).map((m) => [m.id, true])));
  }

  const sinRegla = lote ? lote.movimientos.filter((m) => !m.propuesta && !m.en_mes_cerrado && !propuestasIA[m.id]) : [];

  async function pedirIA() {
    setPidiendoIA(true);
    setError('');
    setAviso('');
    try {
      const { data } = await api.post('/categorizacion-reglas/lote/ia', { ids: sinRegla.slice(0, 300).map((m) => m.id) });
      let t;
      do {
        await esperar(2500);
        ({ data: t } = await api.get(`/categorizacion-reglas/lote/ia/${data.job_id}`));
      } while (t.estado === 'procesando');
      if (t.estado === 'error') setError(t.error);
      else {
        setPropuestasIA((prev) => ({ ...prev, ...Object.fromEntries(t.propuestas.map((p) => [p.movimiento_id, p])) }));
        setAviso(t.propuestas.length
          ? `La IA propuso categoría para ${t.propuestas.length}; vienen sin marcar, revísalas.`
          : 'La IA no encontró una categoría que encaje para estos movimientos.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'La IA no respondió; intenta de nuevo.');
    } finally {
      setPidiendoIA(false);
    }
  }

  const propuestaDe = (m) => m.propuesta || propuestasIA[m.id] || null;
  const aAplicar = lote ? lote.movimientos.filter((m) => marcados[m.id] && propuestaDe(m)) : [];

  async function aplicarLote() {
    setGuardando(true);
    setError('');
    try {
      const { data } = await api.post('/categorizacion-reglas/aplicar', {
        items: aAplicar.map((m) => {
          const p = propuestaDe(m);
          return { movimiento_id: m.id, categoria_id: p.categoria_id, regla_id: p.regla_id || null, origen: p.origen };
        })
      });
      setAviso(`${data.aplicados} movimiento${data.aplicados === 1 ? '' : 's'} con su categoría puesta${data.saltados ? ` (${data.saltados} saltados)` : ''}.`);
      setLote(null);
      cargar();
      contarSin();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo aplicar');
    } finally {
      setGuardando(false);
    }
  }

  const activas = reglas.filter((r) => r.activa).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          {activas} regla{activas === 1 ? '' : 's'} activa{activas === 1 ? '' : 's'}. Si dos coinciden con el mismo movimiento, gana la más específica (texto más largo, luego más filtros).
          Una regla nunca cambia una categoría ya puesta sin que lo marques.
        </p>
        {puede && (
          <div className="flex items-center gap-2">
            {totalSin > 0 && (
              <button type="button" onClick={abrirLote}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700"
                title="Propone categoría a los movimientos que no tienen; revisas antes de aplicar">
                <Sparkles size={15} /> Aplicar a los {totalSin} sin categoría
              </button>
            )}
            <button type="button" onClick={() => abrirForm({ ...FORM_VACIO })} title="Nueva regla"
              className="p-2 rounded-md text-white bg-primary-600 hover:bg-primary-700">
              <Plus size={18} />
            </button>
          </div>
        )}
      </div>

      {aviso && <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{aviso}</p>}
      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      {form && (
        <section className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-700">{form.id ? 'Editar regla' : 'Nueva regla'}</h2>
            <button type="button" onClick={() => setForm(null)} title="Cerrar" className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="text-sm text-slate-600 space-y-1">
              <span>Buscar en</span>
              <select id="regla-campo" value={form.campo} onChange={(e) => setForm({ ...form, campo: e.target.value })} className={inputCls}>
                {Object.entries(CAMPOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="text-sm text-slate-600 space-y-1">
              <span>Contiene el texto</span>
              <input id="regla-texto" autoFocus value={form.texto} maxLength={100} placeholder="Ej. GAS COMUNAL"
                onChange={(e) => setForm({ ...form, texto: e.target.value })} className={`${inputCls} font-mono`} />
            </label>
            <label className="text-sm text-slate-600 space-y-1">
              <span>Categoría (cuenta contable)</span>
              <select id="regla-categoria" value={form.categoria_id} onChange={(e) => setForm({ ...form, categoria_id: e.target.value })} className={inputCls}>
                <option value="">Elegir…</option>
                {opciones.map((g) => (
                  <optgroup key={g.tipo} label={g.tipo === 'egreso' ? 'Egresos' : 'Ingresos'}>
                    {g.items.map((o) => <option key={o.id} value={o.id}>{o.texto}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
            <label className="text-sm text-slate-600 space-y-1">
              <span>Cuenta bancaria (opcional)</span>
              <select id="regla-cuenta" value={form.cuenta_id || ''} onChange={(e) => setForm({ ...form, cuenta_id: e.target.value })} className={inputCls}>
                <option value="">Todas</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </label>
            <div className="text-sm text-slate-600 space-y-1">
              <span>Monto entre (opcional)</span>
              <div className="flex items-center gap-2">
                <input id="regla-min" type="number" step="0.01" value={form.monto_min ?? ''} onChange={(e) => setForm({ ...form, monto_min: e.target.value })} className={inputCls} aria-label="Monto mínimo" />
                <span>y</span>
                <input id="regla-max" type="number" step="0.01" value={form.monto_max ?? ''} onChange={(e) => setForm({ ...form, monto_max: e.target.value })} className={inputCls} aria-label="Monto máximo" />
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500">El tipo (ingreso o egreso) sale de la categoría. No importan mayúsculas ni acentos.</p>

          {vista && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-2">
              <p className="font-medium text-slate-700">
                Coincide con {vista.total} movimiento{vista.total === 1 ? '' : 's'} de los últimos 6 meses
                {vista.total > 0 && `: ${vista.misma} ya en esta categoría, ${vista.otras.length} en otra, ${vista.sin_categoria.length} sin categoría`}.
              </p>
              {vista.sin_categoria.length > 0 && (
                <label className="flex items-start gap-2">
                  <input type="checkbox" checked={aplicarSin} onChange={(e) => setAplicarSin(e.target.checked)} className="mt-0.5" />
                  Ponerle esta categoría a los {vista.sin_categoria.length} que no tienen
                </label>
              )}
              {vista.otras.length > 0 && (
                <>
                  <label className="flex items-start gap-2">
                    <input type="checkbox" checked={cambiarOtras} onChange={(e) => setCambiarOtras(e.target.checked)} className="mt-0.5" />
                    Cambiar también los {vista.otras.length} que están en otra categoría
                  </label>
                  {cambiarOtras && (
                    <ul className="ml-6 text-xs text-slate-600 space-y-0.5 max-h-48 overflow-y-auto">
                      {vista.otras.map((m) => (
                        <li key={m.id} className={m.en_mes_cerrado ? 'line-through text-slate-400' : ''}>
                          {formatFecha(m.fecha)} · {m.descripcion || m.nombre_receptor} · {pref(m.moneda)}{formatMonto(m.monto)} · hoy en {m.categoria_codigo ? `${m.categoria_codigo} ` : ''}{m.categoria_nombre}
                          {m.en_mes_cerrado && ' (mes cerrado, no se cambia)'}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setForm(null)} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancelar</button>
            <button type="button" onClick={guardar} disabled={guardando || form.texto.trim().length < 2 || !form.categoria_id}
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
              {guardando ? 'Guardando…' : 'Guardar regla'}
            </button>
          </div>
        </section>
      )}

      {lote && (
        <section className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-slate-700">Aplicar a los {lote.total} sin categoría</h2>
            <div className="flex items-center gap-3">
              {sinRegla.length > 0 && (
                <button type="button" onClick={pedirIA} disabled={pidiendoIA}
                  className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-violet-700 border border-violet-200 hover:bg-violet-50 disabled:opacity-50">
                  {pidiendoIA ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {pidiendoIA ? 'Pidiendo a la IA…' : (sinRegla.length === 1 ? 'Sugerir con IA el que no tiene regla' : `Sugerir con IA los ${Math.min(sinRegla.length, 300)} sin regla`)}
                </button>
              )}
              <button type="button" onClick={() => setLote(null)} title="Cerrar" className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
          </div>
          {lote.total > lote.movimientos.length && <p className="text-xs text-slate-500">Se muestran los {lote.movimientos.length} más recientes; al aplicar, vuelve a abrirlo para seguir con el resto.</p>}
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-500 border-b sticky top-0 bg-white">
                <tr><th className="py-2 pr-2 w-8"></th><th className="py-2 pr-3">Movimiento</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Categoría que le pondría</th><th className="py-2 pr-3">Por</th></tr>
              </thead>
              <tbody>
                {lote.movimientos.map((m) => {
                  const p = propuestaDe(m);
                  return (
                    <tr key={m.id} className={`border-b last:border-0 align-top ${m.en_mes_cerrado ? 'opacity-50' : ''}`}>
                      <td className="py-2 pr-2">
                        <input type="checkbox" disabled={!p || m.en_mes_cerrado} checked={!!marcados[m.id] && !!p}
                          onChange={(e) => setMarcados({ ...marcados, [m.id]: e.target.checked })} aria-label="Aplicar" />
                      </td>
                      <td className="py-2 pr-3">
                        <div className="text-slate-700">{formatFecha(m.fecha)}{m.nombre_receptor ? ` · ${m.nombre_receptor}` : ''}{m.descripcion ? ` · "${m.descripcion}"` : ''}</div>
                        <div className="text-xs text-slate-400">{m.cuenta_nombre}{m.en_mes_cerrado ? ' · mes cerrado' : ''}</div>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums whitespace-nowrap">{pref(m.moneda)}{formatMonto(m.monto)}</td>
                      <td className="py-2 pr-3">
                        {p ? <>{p.categoria_nombre}{p.motivo && <div className="text-xs text-slate-500">{p.motivo}</div>}</> : <span className="text-slate-400">Sin propuesta</span>}
                      </td>
                      <td className="py-2 pr-3">{p && <Etiqueta origen={p.origen} />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">Las de regla vienen marcadas; las de la IA, sin marcar para que las mires. Los de meses cerrados no se cambian.</p>
            <button type="button" onClick={aplicarLote} disabled={guardando || aAplicar.length === 0}
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
              {guardando ? 'Aplicando…' : `Aplicar a ${aAplicar.length}`}
            </button>
          </div>
        </section>
      )}

      <section className="bg-white rounded-xl shadow-sm overflow-hidden">
        {reglas.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500">
            Todavía no hay reglas. Crea la primera con ＋, o desde un movimiento con "Crear regla con esto".
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 text-left text-slate-500">
                <tr><th className="p-3">Si…</th><th className="p-3">Entonces va a</th><th className="p-3 text-right">Usada</th><th className="p-3">Activa</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {reglas.map((r) => (
                  <tr key={r.id} className={`border-t align-top ${r.activa ? '' : 'opacity-50'}`}>
                    <td className="p-3">
                      <span className="text-xs text-slate-500">{CAMPOS[r.campo]} contiene</span>{' '}
                      <span className="font-mono text-xs rounded bg-primary-50 text-primary-700 px-1.5 py-0.5">{r.texto}</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <span className="text-[11px] rounded-full border border-slate-200 px-2 text-slate-500">{r.tipo === 'egreso' ? 'Egreso' : 'Ingreso'}</span>
                        {r.cuenta_nombre && <span className="text-[11px] rounded-full border border-slate-200 px-2 text-slate-500">{r.cuenta_nombre}</span>}
                        {(r.monto_min != null || r.monto_max != null) && (
                          <span className="text-[11px] rounded-full border border-slate-200 px-2 text-slate-500">
                            {r.monto_min != null && r.monto_max != null ? `${formatMonto(r.monto_min)} a ${formatMonto(r.monto_max)}` : r.monto_min != null ? `desde ${formatMonto(r.monto_min)}` : `hasta ${formatMonto(r.monto_max)}`}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {r.categoria_codigo && <span className="mr-2 font-mono text-xs text-slate-400">{r.categoria_codigo}</span>}
                      {r.categoria_padre ? `${r.categoria_padre} › ` : ''}{r.categoria_nombre}
                    </td>
                    <td className="p-3 text-right tabular-nums">{r.veces}</td>
                    <td className="p-3">
                      <button type="button" role="switch" aria-checked={!!r.activa} disabled={!puede} onClick={() => alternar(r)}
                        title={r.activa ? 'Desactivar' : 'Activar'}
                        className={`relative h-5 w-9 rounded-full transition-colors disabled:opacity-50 ${r.activa ? 'bg-green-500' : 'bg-slate-300'}`}>
                        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${r.activa ? 'left-[18px]' : 'left-0.5'}`} />
                      </button>
                    </td>
                    <td className="p-3">
                      {puede && (
                        <div className="flex items-center justify-end gap-3">
                          <button type="button" title="Editar" className="text-slate-400 hover:text-primary-600"
                            onClick={() => abrirForm({ id: r.id, campo: r.campo, texto: r.texto, categoria_id: r.categoria_id, cuenta_id: r.cuenta_id || '', monto_min: r.monto_min ?? '', monto_max: r.monto_max ?? '' })}>
                            <Pencil size={16} />
                          </button>
                          <button type="button" title="Eliminar" className="text-slate-400 hover:text-red-600" onClick={() => eliminar(r)}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
